import airbrake from 'common/airbrake';
import { getCurrentDomain } from 'common/currentDomain';
import { fetchJsonWithParsedError } from 'common/http';
import {
  fetchAllSubscriptionsForAsset,
  deleteAllForAsset,
  ScreenshotSubscription,
} from 'common/notifications/api/ScreenshotSubscriptionsAPI';
import { CatalogInteractiveUser } from 'common/types/users/catalogUsers';
import { View } from 'common/types/view';
import InteractiveUser from 'common/types/users/interactiveUser';
import { ScheduleSendValues } from './types/scheduleSend';
import { buildPayload } from './builder';
import { showForgeSuccessToastNow } from '../ToastNotification/Toastmaster';
import { I18n } from 'common/i18n';
import {
  createScheduleDelivery,
  ScheduleDeliverySubscription,
  updateScheduleDelivery,
} from 'common/notifications/api/ScheduleDeliveryAPI';
import {
  isScheduleDeliverySubscription,
  ScheduleDeliverySubscriptionWithType,
  ScreenshotSubscriptionWithType,
  SubscriptionWithType,
} from './types/subscription';
import { errorHandler, ErrorHandlerResult } from './errorHandler';

const scope = 'shared.components.schedule_send_report.modal';

//Assign type on objects
const createScheduleDeliveryWithType = (
  subscription: ScheduleDeliverySubscription,
): ScheduleDeliverySubscriptionWithType => {
  return { ...subscription, type: 'scheduled_delivery' };
};

const createScreenshotWithType = (subscription: ScreenshotSubscription): ScreenshotSubscriptionWithType => {
  return { ...subscription, type: 'watch' };
};

export const fetchExistingSubscriptions = async (): Promise<SubscriptionWithType[]> => {
  try {
    const data = (await fetchAllSubscriptionsForAsset(window.STORY_DATA.uid).then((res) => res.data)) ?? [];

    // Assign type field based on subscription structure
    return data.map((subscription): SubscriptionWithType => {
      if (isScheduleDeliverySubscription(subscription)) {
        return createScheduleDeliveryWithType(subscription);
      } else {
        return createScreenshotWithType(subscription);
      }
    });
  } catch {
    return [];
  }
};

export const fetchUsers = async (view: View) => {
  try {
    const params = [
      `domain=${view.domainCName || getCurrentDomain()}`,
      'include_teams=false',
      'only=site_members',
      'disabled=false',
      'future=false',
      'rights=can_collaborate',
    ].join('&');

    const url = `/api/catalog/v1/users?${params}`;
    const response: { results: CatalogInteractiveUser[] } = await fetchJsonWithParsedError(url);
    return response.results;
  } catch (error) {
    airbrake.notify({ error, context: { component: 'ScheduleSendReport: fetchUsers()' } });
    return [];
  }
};

export const handleScheduleDeliveryRequest = async (
  apiCall: (payload: ScheduleDeliverySubscription) => Promise<any>,
  scheduleSendValues: ScheduleSendValues,
  currentUser: InteractiveUser,
  setScheduledSendModalVisible: (value: React.SetStateAction<boolean>) => void,
  successToastKey: string,
  errorLogPrefix: string,
) => {
  try {
    const payload = buildPayload(scheduleSendValues, currentUser);
    await apiCall(payload);
    showForgeSuccessToastNow(I18n.t(successToastKey, { scope }));
    setScheduledSendModalVisible(false);
  } catch (error) {
    return errorHandler(error, errorLogPrefix);
  }
};

export const postScheduleDeliveryRequest = async (
  scheduleSendValues: ScheduleSendValues,
  currentUser: InteractiveUser,
  setScheduledSendModalVisible: (value: React.SetStateAction<boolean>) => void,
) => {
  return handleScheduleDeliveryRequest(
    createScheduleDelivery,
    scheduleSendValues,
    currentUser,
    setScheduledSendModalVisible,
    'success_toast',
    'Error creating schedule delivery',
  );
};

export const updateScheduleDeliveryRequest = async (
  scheduleSendValues: ScheduleSendValues,
  currentUser: InteractiveUser,
  setScheduledSendModalVisible: (value: React.SetStateAction<boolean>) => void,
) => {
  return handleScheduleDeliveryRequest(
    updateScheduleDelivery,
    scheduleSendValues,
    currentUser,
    setScheduledSendModalVisible,
    'update_toast',
    'Error updating schedule delivery',
  );
};

export const deleteScheduleDelivery = async (): Promise<ErrorHandlerResult> => {
  const fourFour = window.STORY_DATA.uid;
  try {
    const response = await deleteAllForAsset(fourFour);
    if (!response.ok) {
      return { success: false, error: response };
    }
    return { success: true, error: undefined };
  } catch (error) {
    return errorHandler(error, 'Error deleting schedule delivery');
  }
};
